import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';
import {
  COMPOSITION_GET_DATA_V2,
  COMPOSITION_GET_DATA_V3,
  type CompositionV2DataQuery,
  type CompositionV2DataQueryVariables,
  type CompositionV2Entity,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
  type GetPayV2Query,
  PAY_V2_QUERY,
  type PayV2Entity,
  type PayV2Req,
} from '@revelio/data-access';

import { environment } from '../../../../../environments/environment';
import { useSelectionListState } from '../../../../../shared/selection-lists/hooks/useSelectionListState';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../../filters/hooks/useGeographyMode';
import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import { getCompositionV3Variables } from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { getMonthRefsFromCompositionV3Filters } from '../../../filters/utils/composition-v3/month-refs-from-v3-filters';
import { mapCompositionV2EntitiesToV3 } from '../../../filters/utils/composition-v3/role-skill-ent-kind';
import { getCompositionV2Filters } from '../../../filters/utils/getCompositionV2Filters';
import { getV2Filters } from '../../../filters/utils/getV2Filters';
import { adaptCompositionV3ToLegacyCompositionV2ForTreemap } from '../composition/composition-v3/adapt-composition-v3-to-legacy-treemap';
import { isCompositionV3FullMetrics } from '../composition/composition-v3/composition-v3-graphql-response';
import { combineTwoQueryStatuses } from '../market-conditions/market-condition-metrics-utils';
import { DataProviderQueryStatus } from '../types';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';
import { getSegmentEntityType } from './getSegmentEntityType';

const isProd = environment.production;

const MAX_SEGMENT_ENTITIES = isProd ? 6 : 10;

type CompositionApiMode = 'legacy' | 'v3';

function usePayAndCompositionQueriesInner(
  compositionApi: CompositionApiMode,
  {
    primaryDimension,
    secondaryDimension,
    entitiesOverride,
  }: {
    primaryDimension: PrimaryFilterId;
    secondaryDimension: 'geography' | 'role';
    entitiesOverride?: CompositionV2Entity[];
  }
) {
  const isV3Api = compositionApi === 'v3';
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { selectionLists } = useSelectionListState();
  const geographyMode = useGeographyMode();

  const compositionVariables = getCompositionV2Filters({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    selectionLists: selectionLists ?? undefined,
    geographyMode,
  });

  const compositionVariablesV3 = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    selectionLists: selectionLists ?? undefined,
    geographyMode,
  });

  const compositionEntities = (() => {
    if (Array.isArray(entitiesOverride) && entitiesOverride.length > 0) {
      return entitiesOverride;
    }
    if (Array.isArray(compositionVariables.entities)) {
      return compositionVariables.entities;
    }
    return [compositionVariables.entities];
  })();

  const compositionEntitiesV3 = (() => {
    if (Array.isArray(entitiesOverride) && entitiesOverride.length > 0) {
      return mapCompositionV2EntitiesToV3(entitiesOverride) ?? [];
    }
    if (Array.isArray(compositionVariablesV3.entities)) {
      return compositionVariablesV3.entities;
    }
    return compositionVariablesV3.entities
      ? [compositionVariablesV3.entities]
      : [];
  })();

  const breakdownOptionsSingle = (() => {
    const opts = compositionVariables.breakdownOptions;
    if (secondaryDimension === 'geography' && !!opts?.Geography) {
      return { Geography: opts.Geography };
    }
    if (secondaryDimension === 'role' && !!opts?.Role) {
      return { Role: opts.Role };
    }
    return undefined;
  })();

  const compositionVariablesFinal = {
    ...compositionVariables,
    entities: compositionEntities,
    breakdownOptions:
      breakdownOptionsSingle ?? compositionVariables.breakdownOptions,
  };

  const compositionVariablesFinalV3 = {
    ...compositionVariablesV3,
    entities: compositionEntitiesV3,
  };

  const pauseComposition =
    compositionVariablesFinal.entities.length === 0 ||
    !compositionVariablesFinal.filters?.start_date ||
    !compositionVariablesFinal.filters?.end_date;

  const pauseCompositionV3 =
    compositionVariablesFinalV3.entities.length === 0 ||
    !compositionVariablesFinalV3.filters?.start_date ||
    !compositionVariablesFinalV3.filters?.end_date;

  const compositionQuery = isV3Api
    ? COMPOSITION_GET_DATA_V3
    : COMPOSITION_GET_DATA_V2;
  const compositionVariablesActive = isV3Api
    ? compositionVariablesFinalV3
    : compositionVariablesFinal;
  const pauseCompositionActive = isV3Api
    ? pauseCompositionV3
    : pauseComposition;

  const [{ data: compositionDataUnion, status: compositionStatusUnion }] =
    useUrqlQueryWithStatus<
      CompositionV2DataQuery | CompositionV3DataQuery,
      CompositionV2DataQueryVariables | CompositionV3DataQueryVariables
    >({
      query: compositionQuery,
      variables: compositionVariablesActive as CompositionV2DataQueryVariables &
        CompositionV3DataQueryVariables,
      pause: pauseCompositionActive,
    });

  const compositionRes = isV3Api
    ? (compositionDataUnion as CompositionV3DataQuery | undefined)
        ?.composition_v3
    : (compositionDataUnion as CompositionV2DataQuery | undefined)
        ?.composition_v2;

  const segmentEntityType = compositionRes
    ? getSegmentEntityType({
        secondaryDimension,
        compositionFilters: compositionVariablesFinal.filters,
      })
    : null;

  const segmentPayEntities: PayV2Entity[] = (() => {
    if (!compositionRes?.entities?.length || segmentEntityType === null) {
      return [];
    }
    const seen = new Set<number>();
    const out: PayV2Entity[] = [];
    for (const ent of compositionRes.entities) {
      const segments =
        (isV3Api
          ? (isCompositionV3FullMetrics(ent.metrics)
              ? ent.metrics
              : undefined)?.[secondaryDimension]
          : ent.metrics?.[secondaryDimension]) ?? [];
      for (const seg of segments) {
        if (seen.size >= MAX_SEGMENT_ENTITIES) break;
        const id = (() => {
          if (isV3Api) {
            return (seg as { id: number }).id;
          }
          if (typeof seg === 'object' && seg !== null && 'metadata' in seg) {
            return seg.metadata?.id;
          }
          return undefined;
        })();
        if (id !== undefined && !seen.has(id)) {
          seen.add(id);
          out.push({ EntID: id, EntType: segmentEntityType });
        }
      }
      if (seen.size >= MAX_SEGMENT_ENTITIES) break;
    }
    return out;
  })();

  const payFilterPrimaryDimension =
    secondaryDimension === 'geography' ? FilterId.GEOGRAPHY : FilterId.ROLE;
  const payFilters = getV2Filters({
    primaryDimension: payFilterPrimaryDimension,
    effectiveFilters,
    isSnapshot: false,
  });

  const payVariables: PayV2Req = {
    entities: segmentPayEntities,
    filters: payFilters,
    ...(geographyMode === 'administrative' && {
      options: { geo_breakdown_state_over_msa: true },
    }),
  };

  const compositionOk =
    !pauseCompositionActive &&
    compositionStatusUnion === DataProviderQueryStatus.Success;

  const pausePay =
    pauseCompositionActive ||
    !compositionOk ||
    !compositionRes ||
    segmentPayEntities.length === 0 ||
    !payVariables.filters?.start_date ||
    !payVariables.filters?.end_date;

  const [{ data: payData, status: payStatus }] =
    useUrqlQueryWithStatus<GetPayV2Query>({
      query: PAY_V2_QUERY,
      variables: { req: payVariables },
      pause: pausePay,
    });

  const status = combineTwoQueryStatuses(compositionStatusUnion, payStatus);

  const compositionDataForCharts = (() => {
    if (isV3Api) {
      const v21 = (compositionDataUnion as CompositionV3DataQuery | undefined)
        ?.composition_v3;
      if (!v21) {
        return undefined;
      }
      return adaptCompositionV3ToLegacyCompositionV2ForTreemap(
        v21,
        getMonthRefsFromCompositionV3Filters(
          compositionVariablesFinalV3.filters
        ),
        compositionEntitiesV3[0]?.EntType
      );
    }
    return (compositionDataUnion as CompositionV2DataQuery | undefined)
      ?.composition_v2;
  })();

  return {
    payData: payData?.pay_v2 ?? undefined,
    compositionData: compositionDataForCharts,
    status,
  };
}

type PayAndCompositionParams = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: 'geography' | 'role';
  entitiesOverride?: CompositionV2Entity[];
};

export function usePayAndCompositionQueriesLegacy(
  params: PayAndCompositionParams
) {
  return usePayAndCompositionQueriesInner('legacy', params);
}

export function usePayAndCompositionQueriesV3(params: PayAndCompositionParams) {
  return usePayAndCompositionQueriesInner('v3', params);
}

export function usePayAndCompositionQueries(params: PayAndCompositionParams) {
  const isV3 = useUnleashFlag(FeatureFlag.CompositionV3);
  return usePayAndCompositionQueriesInner(isV3 ? 'v3' : 'legacy', params);
}
